import React, { useMemo, useState } from 'react';
import { useMutation } from 'urql';

import { Button, Counter, LoginGateBlock, QtyField, ShareBlock } from '@/components';
import Select from '@/components/forms/Select';
import { useIsMobile, useStore } from '@/hooks';
import { trackEvent, trackProduct } from '@/lib/GA';
import { extractItems } from '@/lib/GraphQLHelper';
import { currencyFormat } from '@/lib/Utils';
import { BlackhawkGiftCard } from '@/stores/Cart';

import HeartFilledIcon from '@/images/icons/heart--filled.svg';
import HeartIcon from '@/images/icons/heart.svg';

import mutation from './mutation.graphql';

import styles from './styles.module.scss';

interface Props {
  product: BlackhawkGiftCard;
  inWishlist?: boolean;
}

const AddToCartBlock = ({ product, inWishlist }: Props) => {
  const { addToCraftCommerce } = useStore('cart');
  const { loggedIn } = useStore('userStore');
  const [isMobile] = useIsMobile(768);

  const [qty, setQty] = useState(1);
  const [goToCart, setGoToCart] = useState(false);
  const [, mutate] = useMutation(mutation);
  const [addedToWishlist, setAddedToWishlist] = useState(inWishlist || false);
  const [currentVariant, setCurrentVariant] = useState<Nullable<GiftCardVariantFragment>>(null);

  const variants = useMemo(() => {
    return extractItems<GiftCardVariantFragment>(product.variants);
  }, [product.variants]);

  const handleAddToWishlist = () => {
    setAddedToWishlist(!addedToWishlist);
    mutate({ id: product.id });
    trackEvent(
      'Wishlist',
      `Clicked ${!addedToWishlist ? 'add to wish list' : 'remove from wishlist'}`,
      `${product.title} Gift Card`,
    );
  };

  const handleAddToCart = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loggedIn && currentVariant) {
      const ind = variants?.findIndex((v) => v?.id === currentVariant?.id);
      await addToCraftCommerce(product as GiftCardFragment, qty, ind);

      trackProduct(
        {
          id: product.id!,
          name: `${product.title!} - $${currentVariant.fullValue} Gift Card`,
          category: 'Gift Cards',
          price: currentVariant.fullValue ?? 0,
          quantity: qty,
        },
        'addToCart',
      );

      setGoToCart(true);
    }
  };

  const handleCounterChange = (qty: number) => {
    // Counter Quantity Change (For Mobile)
    setQty(qty);
  };

  const buyitNow = async () => {
    if (loggedIn && currentVariant) {
      const ind = variants?.findIndex((v) => v?.id === currentVariant?.id);
      await addToCraftCommerce(product as GiftCardFragment, qty, ind);
      window.location.assign('/cart');
    }
  };

  const variantOptions =
    variants
      ?.filter((f) => !!f.id)
      .map(
        (v): Option => ({
          title: `$${v.fullValue}`,
          value: v.id!,
        }),
      ) ?? [];

  const renderContent = () => {
    if (isMobile) {
      return (
        <div className={styles.mobileBlock}>
          <h2>Get a gift card for any amount</h2>
          <div className={styles.options}>
            <Select
              name="Variants"
              options={[
                {
                  title: 'Choose your giftcard value',
                  value: '',
                },
                ...variantOptions,
              ]}
              onChange={(e) => setCurrentVariant(variants?.find((v) => v.id === e.target.value) ?? null)}
            />
            <Counter number={qty} setNumber={handleCounterChange} min={1} max={10} />
          </div>

          {currentVariant && (
            <div className={styles.mobilePrices}>
              <div className={styles.prices}>
                <h3>{currencyFormat(Number(currentVariant?.price))}</h3>
                <span>{currencyFormat(currentVariant.fullValue)}</span>
              </div>
              <div className={styles.right}>
                {currentVariant.fullValue && currentVariant.price && (
                  <div className={styles.discount}>
                    Save <strong>{product.discount}%</strong>
                  </div>
                )}
                <div className={styles.smallWishlist}>
                  <Button
                    design="highlight-borders"
                    size="large"
                    data-wishlist={addedToWishlist}
                    data-testid="wishlist-btn"
                    onClick={handleAddToWishlist}
                  >
                    {addedToWishlist ? <HeartFilledIcon /> : <HeartIcon />}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleAddToCart}>
            <Button design="highlight-borders" size="large" as="submit" disabled={!currentVariant}>
              Add to cart
            </Button>
          </form>
          {goToCart ? (
            <Button design="pricesavings" size="large" href="/cart">
              View cart
            </Button>
          ) : (
            <Button design="highlight" size="large" onClick={buyitNow} disabled={!currentVariant}>
              Buy now
            </Button>
          )}
        </div>
      );
    } else {
      return (
        <>
          <div className={styles.addToCartBlock}>
            <h3>Get a gift card for any amount</h3>
            <div>
              <div className={styles.cart}>
                <form onSubmit={handleAddToCart}>
                  <div className={styles.variantSelect}>
                    <Select
                      name="Variants"
                      options={[
                        {
                          title: 'Choose your giftcard value',
                          value: '',
                        },
                        ...variantOptions,
                      ]}
                      onChange={(e) => setCurrentVariant(variants?.find((v) => v.id === e.target.value) ?? null)}
                    />
                  </div>
                  {currentVariant && (
                    <div className={styles.prices}>
                      <div className={styles.price}>
                        <span>{currencyFormat(currentVariant?.fullValue)}</span>
                        {currencyFormat(Number(currentVariant?.price))}
                      </div>
                      <div className={styles.discount}>
                        Save <strong>{product.discount}%</strong>
                      </div>
                    </div>
                  )}
                  <QtyField onChange={setQty} />
                  <Button design="highlight-borders" size="large" as="submit" disabled={!currentVariant}>
                    Add to cart
                  </Button>
                </form>
              </div>
              <div className={styles.buttons}>
                {goToCart ? (
                  <Button design="pricesavings" size="large" href="/cart">
                    View cart
                  </Button>
                ) : (
                  <Button design="highlight" size="large" onClick={buyitNow} disabled={!currentVariant}>
                    Buy now
                  </Button>
                )}
                <Button
                  design="highlight-borders"
                  size="large"
                  data-wishlist={addedToWishlist}
                  data-testid="wishlist-btn"
                  onClick={handleAddToWishlist}
                >
                  {addedToWishlist ? 'Remove from' : 'Add to'} wishlist
                </Button>
              </div>
            </div>
          </div>
          {!isMobile && <ShareBlock />}
        </>
      );
    }
  };

  if (!loggedIn) {
    return <LoginGateBlock>You must be a member to purchase this product</LoginGateBlock>;
  }

  return <div>{renderContent()}</div>;
};

export default AddToCartBlock;
